/**
 * The frame's banners, in the page's flow at the top, so they take their own
 * height out of the column rather than covering a widget's header.
 *
 * - **A connection that died** (F2 (a), settled 2026-10-05): a banner, as v1
 *   had for Google, for any provider whose refresh token was refused —
 *   `auth_invalid` on the stream, or the status on open. Reconnect starts the
 *   flow; ✕ hides it until the next refusal. Settings marks it too.
 * - **The server's disk is almost full** (#70): to the owner only, read every
 *   ten minutes. On 2026-10-21 it filled, and everything failed with no word.
 *   ✕ hides it until the page is opened again.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../api/client";
import { ok } from "../api/query";
import { useMe } from "../app/me";
import { FLOWS, providerName } from "../widgets/settings/providers";
import styles from "./Banners.module.css";
import { CONNECTIONS, useConnections } from "./connections";
import { useLive } from "./live";

export function Banners() {
  return (
    <>
      {useMe().owner && <DiskBanner />}
      <ReconnectBanner />
    </>
  );
}

const GIB = 1024 ** 3;

function DiskBanner() {
  const [hidden, setHidden] = useState(false);
  const disk = useQuery({
    queryKey: ["disk"],
    queryFn: () => ok(api.GET("/api/disk")),
    refetchInterval: 10 * 60 * 1000,
  });
  if (hidden || !disk.data?.low) return null;
  const free = (disk.data.free_bytes / GIB).toFixed(1);
  return (
    <div className={styles.reconnect} role="alert">
      <span className={styles.message}>
        The server&apos;s disk is almost full: {free} GB free. When it is full, Kindred stops
        working.
      </span>
      <button className={styles.dismiss} onClick={() => setHidden(true)} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
}

function ReconnectBanner() {
  const client = useQueryClient();
  const status = useConnections();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  useLive("auth_invalid", ({ provider }) => {
    setHidden((was) => new Set([...was].filter((p) => p !== provider)));
    void client.invalidateQueries({ queryKey: CONNECTIONS });
  });
  const dead = Object.entries(status.data ?? {})
    .filter(([provider, row]) => row.needs_reconnect && !hidden.has(provider))
    .map(([provider]) => provider);
  if (!dead.length) return null;
  return (
    <>
      {dead.map((provider) => (
        <div key={provider} className={styles.reconnect} role="alert">
          <span className={styles.message}>
            {providerName(provider)} sync stopped — your access needs renewing.
          </span>
          <div className={styles.actions}>
            {FLOWS.has(provider) && (
              <a className={styles.reconnectLink} href={`/oauth/${provider}/start`}>
                Reconnect {providerName(provider)}
              </a>
            )}
            <button
              className={styles.dismiss}
              onClick={() => setHidden((was) => new Set([...was, provider]))}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </>
  );
}
